import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { calendar } from "@planzone/dto";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceEvent } from "../../common/event";
import { reduceMilestone } from "../../common/milestone";
import { reduceTask } from "../../common/task";
import { handleCreateEventSuccess } from "./createEvent";
import { handleCreateMilestoneSuccess } from "./createMilestone";
import { handlecreateUnavailabilitySuccess } from "./createUnavailability";
import { handleEventRemoval } from "./removeEvent";
import { handleTaskRemoval } from "./removeTask";
import { handleMilestoneRemoval } from "./removeMilestone";
import { handleUnavailabilityRemoval } from "./removeUnavailability";
import {
  handleEditUnavailabilityRequest,
  handleEditUnavailabilitySuccess,
  handleEditUnavailabilityFailure,
} from "./editUnavailability";
import { handleCreateTaskSuccess } from "./createTask";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { match } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  { filter }: Boundaries["retrieveCalendar"]["request"],
): State {
  const index = state.findIndex((req) => match(req.filter, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], filter, request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [...state, { filter, request: setRequestStateToPending(), calendar }];
}

function handleSuccess(
  state: State,
  { calendar, filter }: Boundaries["retrieveCalendar"]["success"],
): State {
  const index = state.findIndex((req) => match(req.filter, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          filter,
          request: setRequestStateToSuccess(),
          calendar,
        },
        ...state.slice(index + 1),
      ]
    : [...state, { filter, request: setRequestStateToSuccess(), calendar }];
}

function handleFailure(
  state: State,
  { filter }: Boundaries["retrieveCalendar"]["failure"],
): State {
  const index = state.findIndex((req) => match(req.filter, filter));

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], filter, request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [...state, { filter, request: setRequestStateToFailure(), calendar }];
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    calendar: {
      ...req.calendar,
      events: req.calendar.events.map((e) => ({
        ...e,
        ...reduceEvent(e, event),
      })),
      milestones: req.calendar.milestones.map((m) => ({
        ...m,
        ...reduceMilestone(m, event),
      })),
      tasks: req.calendar.tasks.map((t) => ({
        ...t,
        ...reduceTask(t, event),
      })),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveCalendarRequested]: handleRequest,
      [events.retrieveCalendarSucceeded]: handleSuccess,
      [events.retrieveCalendarFailed]: handleFailure,
      [events.createEventSucceeded]: handleCreateEventSuccess,
      [events.createMilestoneSucceeded]: handleCreateMilestoneSuccess,
      [events.createUnavailabilitySucceeded]: handlecreateUnavailabilitySuccess,
      [events.removeMilestoneSucceeded]: handleMilestoneRemoval,
      [events.removeEventSucceeded]: handleEventRemoval,
      [events.removeTaskSucceeded]: handleTaskRemoval,
      [events.removeUnavailabilitySucceeded]: handleUnavailabilityRemoval,
      [events.editUnavailabilityRequested]: handleEditUnavailabilityRequest,
      [events.editUnavailabilitySucceeded]: handleEditUnavailabilitySuccess,
      [events.editUnavailabilityFailed]: handleEditUnavailabilityFailure,
      [events.createTaskSucceeded]: handleCreateTaskSuccess,
      [events.createKanbanTaskSucceeded]: handleCreateTaskSuccess,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
